import React, { KeyboardEvent, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ArrowDropDown as ArrowDropDownIcon, InfoTwoTone as InfoIcon } from '@mui/icons-material';
import { styled, Tooltip } from '@mui/material';
import { Grid } from '@mui/system';
import { isEmpty, isObject } from 'lodash';
import Select, {
  components,
  createFilter,
  CSSObjectWithLabel,
  MultiValue,
  OptionProps,
  SingleValue,
  StylesConfig,
} from 'react-select';
import theme from 'theme';
import { toString } from 'utilities';
import {
  CustomSelectProps,
  DropdownIndicatorPropsBase,
  IsMulti,
  OptionPropsBase,
  OptionType,
  StateEvent,
} from './types';

const PREFIX = 'CustomSelect';

const classes = {
  tooltip: `${PREFIX}-tooltip`,
  dropdownIndicator: `${PREFIX}-dropdownIndicator`,
};

const StyledGrid = styled(Grid)(() => ({
  [`& .${classes.dropdownIndicator}`]: {
    padding: '0.16em !important',
    marginLeft: '0.26em',
  },
}));

// JavaScript key codes
const TAB_KEY = 'Tab';
const ENTER_KEY = 'Enter';
const ESCAPE_KEY = 'Escape';

const ITEM_HEIGHT = 28;

const containerStyle = (styles: CSSObjectWithLabel) => ({
  ...styles,
  height: ITEM_HEIGHT,
  marginTop: '2px',
  fontWeight: 400,
  padding: '2px 2px 4px',
});

const selectStyles: StylesConfig<OptionType, IsMulti> = {
  control: styles => ({
    ...styles,
    height: 'auto', // Changed from fixed height to auto
    minHeight: ITEM_HEIGHT,
    width: '100%',
    border: 'none',
    borderColor: 'none',
    borderRadius: 'none',
    backgroundColor: 'white',
    position: 'absolute',
    left: 0,
    top: 0,
    boxShadow: 'none',
  }),
  container: () => ({
    // This is intentional just to remove the default styles on container
  }),
  valueContainer: styles => ({
    ...containerStyle(styles),
    height: 'auto', // Allow height to grow with content
    minHeight: ITEM_HEIGHT - 8, // Subtract padding
  }),
  singleValue: styles => ({
    ...styles,
    color: theme.palette.primary[600],
    marginRight: 0,
    right: '0px',
    top: '50%',
    whiteSpace: 'normal', // Allow text wrapping
    wordWrap: 'break-word', // Break long words if needed
    lineHeight: '1.2', // Better line spacing for wrapped text
  }),
  indicatorSeparator: styles => ({
    ...styles,
    display: 'none',
  }),
  indicatorsContainer: styles => ({
    ...containerStyle(styles),
    padding: '0px',
    alignSelf: 'flex-start', // Align to top when content wraps
  }),
  option: styles => ({
    ...styles,
    padding: '5px 12px',
    whiteSpace: 'normal', // Changed from 'nowrap' to 'normal' to allow wrapping
    overflow: 'visible', // Changed from 'hidden' to 'visible'
    wordWrap: 'break-word', // Break long words if needed
    lineHeight: '1.2', // Better line spacing for wrapped text
    minHeight: '20px', // Ensure minimum height for readability
  }),
  menu: styles => ({
    ...styles,
    left: 0,
    borderRadius: 'none',
    marginTop: '-25px',
    fontWeight: 400,
  }),
  menuList: styles => ({
    ...styles,
    paddingTop: 0,
    paddingBottom: 0,
    bottom: '100%',
  }),
  menuPortal: styles => ({
    ...styles,
    fontSize: 14,
    margin: '-2rem 0 0',
    height: 'auto', // Changed from fixed height to auto
    minHeight: ITEM_HEIGHT,
    color: theme.palette.primary[600],
    zIndex: 9999,
  }),
};

const CustomTooltip = Tooltip;

const StyledGridWithConditionalTooltip = ({
  shouldDisplayTooltip,
  label,
  children,
}: {
  shouldDisplayTooltip: boolean;
  label: string;
  children: React.ReactNode;
}) => shouldDisplayTooltip ? (
  <CustomTooltip placement="bottom-start" title={label}>
    <Grid component="span">{children}</Grid>
  </CustomTooltip>
) : (
  <Grid component="span">{children}</Grid>
);

const Option = (props: OptionPropsBase) => {
  const { children, containerWidth, data, ...rest } = props;
  const { label = '', tooltip = '', isTitle = false } = data;

  const [optionWidth, setOptionWidth] = useState(0);

  const optionRef = useRef<HTMLDivElement | null>(null);

  const shouldDisplayTooltip = useMemo(() => optionWidth > containerWidth, [optionWidth, containerWidth]);

  useLayoutEffect(() => {
    if (optionRef?.current) {
      setOptionWidth(optionRef.current.offsetWidth);
    }
  }, [optionRef]);

  // For title options, render a non-interactive element
  if (isTitle) {
    return (
      <div
        style={{
          padding: '8px 12px',
          fontWeight: 'bold',
          color: theme.palette.text.secondary,
          backgroundColor: theme.palette.grey[50],
          cursor: 'default',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
        ref={optionRef}
      >
        {!isEmpty(tooltip) ? (
          <div style={{ position: 'relative', width: '100%' }}>
            <StyledGridWithConditionalTooltip shouldDisplayTooltip={shouldDisplayTooltip} label={label}>
              {children}
            </StyledGridWithConditionalTooltip>
            <div style={{
              position: 'absolute',
              right: 4,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 1
            }}>
              <Tooltip placement="top-end" title={tooltip} arrow>
                <InfoIcon />
              </Tooltip>
            </div>
          </div>
        ) : (
          <StyledGridWithConditionalTooltip shouldDisplayTooltip={shouldDisplayTooltip} label={label}>
            {children}
          </StyledGridWithConditionalTooltip>
        )}
      </div>
    );
  }

  return (
    <components.Option {...{ ...rest, data }}>
      {!isEmpty(tooltip) ? (
        <div style={{ position: 'relative', width: '100%' }}>
          <StyledGrid ref={optionRef}>
            <StyledGridWithConditionalTooltip shouldDisplayTooltip={shouldDisplayTooltip} label={label}>
              {children}
            </StyledGridWithConditionalTooltip>
          </StyledGrid>
          <div style={{ 
            position: 'absolute', 
            right: 4, 
            top: '50%', 
            transform: 'translateY(-50%)',
            zIndex: 1
          }}>
            <Tooltip placement="top-end" title={tooltip} arrow>
              <InfoIcon />
            </Tooltip>
          </div>
        </div>
      ) : (
        <StyledGrid ref={optionRef}>
          <StyledGridWithConditionalTooltip shouldDisplayTooltip={shouldDisplayTooltip} label={label}>
            {children}
          </StyledGridWithConditionalTooltip>
        </StyledGrid>
      )}
    </components.Option>
  );
};

const DropdownIndicator = (props: DropdownIndicatorPropsBase) => (
  <components.DropdownIndicator {...props} className={classes.dropdownIndicator}>
    <ArrowDropDownIcon />
  </components.DropdownIndicator>
);

const CustomSelect = ({
  value,
  options,
  onEmptyValue,
  onChange,
  openMenuOnFocus = true,
  setLabelAsValue,
  disabled,
  onCommit,
  onRevert,
  enumerated = false,
  autoFocus = true,
  menuPlacement,
  usePortal,
  menuPosition = 'absolute',
  extraStyles,
  isMulti = false,
}: CustomSelectProps) => {
  const [selectedValue, setSelectedValue] = useState<OptionType | OptionType[]>();
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [state, setState] = useState<StateEvent>({ e: null });

  const containerRef = useRef<HTMLDivElement | null>(null);

  const optionList = useMemo(() => {
    const tmpList: OptionType[] = [];

    if (options) {
      options.forEach((option, index) => {
        let parsedOption = option;

        if (!isObject(option)) {
          parsedOption = {
            value: toString(index),
            label: toString(option),
          };
        }

        if (parsedOption) {
          tmpList.push(parsedOption);
        }
      });
    }

    return tmpList;
  }, [options]);

  const handleChange = (newValue: SingleValue<OptionType> | MultiValue<OptionType>) => {
    if (isMulti) {
      const list = (newValue as MultiValue<OptionType>) || [];
      const selectedValues = list.map(opt => (enumerated ? opt?.label : toString(opt?.value)));
      const { e } = state;
      onCommit?.(selectedValues, e);
      onChange?.(selectedValues);
      return;
    }

    const opt = newValue as OptionType;
    const selected = enumerated ? opt?.label : toString(opt?.value);

    if (!opt && onRevert) {
      onRevert();
      return;
    }

    const { e } = state;
    onCommit?.(selected, e);

    onChange?.(selected);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    // Abort edition
    if (e.key === ESCAPE_KEY && onRevert) {
      onRevert();
    }

    if ([ENTER_KEY, TAB_KEY].includes(e.key)) {
      e.persist();
      setState({ e });
    } else {
      setState({ e: null });
    }
  };

  useLayoutEffect(() => {
    if (containerRef?.current) {
      setContainerWidth(containerRef.current.clientWidth);
    }
  }, [containerRef]);

  useEffect(() => {
    /*
     * These validations are intended for the Select component when we use it directly
     * bypassing the SelectValueViewer. In these cases, we need to set the option label
     * manually here (currently, only in the Fund Ownership and Allocation pages).
     */
    if (isMulti && Array.isArray(value)) {
      const valuesArray = value as (string | number)[];
      const selectedOptions = valuesArray
        .map(v => optionList.find(opt => toString(opt.value) === toString(v)))
        .filter(Boolean) as OptionType[];
      setSelectedValue(selectedOptions);
      return;
    }

    let itemLabel = '';
    let itemValue = null;
    const itemSelected = optionList.find(opt => toString(opt.value) === toString(value));
    // Fund Ownership: In the Shares Ledger modal, for the date field used in the Purchased section.
    if (setLabelAsValue) {
      itemLabel = (value as string) ?? onEmptyValue;
      itemValue = (value as string) ?? null;
    } else {
      itemLabel = itemSelected?.label ?? onEmptyValue;
      itemValue = itemSelected?.value ?? itemSelected?.id ?? null;
    }
    setSelectedValue({ value: itemValue, label: itemLabel });
  }, [value, setLabelAsValue, optionList, onEmptyValue, isMulti]);

  return (
    <Grid ref={containerRef}>
      <Select
        autoFocus={autoFocus}
        closeMenuOnSelect={!isMulti}
        components={{
          Option: (props: OptionProps<OptionType, IsMulti>) => Option({ ...props, containerWidth }),
          DropdownIndicator,
        }}
        filterOption={createFilter({ matchFrom: 'start' })}
        instanceId="scalar"
        isDisabled={disabled || isEmpty(optionList)}
        isMulti={isMulti}
        isOptionDisabled={option => !!option.isTitle}
        menuPlacement={menuPlacement}
        menuPosition={menuPosition}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        openMenuOnClick
        openMenuOnFocus={openMenuOnFocus}
        options={optionList}
        placeholder={onEmptyValue}
        styles={{ ...selectStyles, ...extraStyles }}
        value={selectedValue}
        {...(usePortal && { menuPortalTarget: document.body })}
      />
    </Grid>
  );
};

export default CustomSelect;
